/**
 * Create Invoice from Multiple Work Orders
 * Allows selecting multiple completed work orders from same organization to create one invoice
 */

import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Checkbox,
  Divider,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Receipt as InvoiceIcon,
  Work as WorkOrderIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import useOrganizationSearch from '../hooks/useOrganizationSearch';
import { isPlatformLevel } from '../utils/roleUtils';
import { getBrandedTableHeadSx } from '../utils/colorUtils';

// Font sizes to match sidebar
const fontSize = {
  sm: '0.875rem',
  xs: '0.75rem',
  md: '1rem'
};

const CreateInvoiceFromWorkOrders = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isPlatform = isPlatformLevel(currentUser?.role);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenterId, setSelectedTypingCenterId] = useState('');
  // Internal organizations (used to filter work orders)
  const {
    options: internalOrganizations,
    loading: loadingOrgs,
    onInputChange: onOrganizationInputChange,
    filterOptions: organizationFilterOptions,
    searchTerm: organizationSearchTerm
  } = useOrganizationSearch({
    typingCenterId: selectedTypingCenterId,
    mapOption: (org) => ({
      id: org.id,
      name: org.name || org.organization_name,
      code: org.code || org.organization_code || ''
    })
  });
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  // Mapped organizations (used for billing — stored as invoice.organizationId)
  const [mappedOrganizations, setMappedOrganizations] = useState([]);
  const [selectedMappedOrganization, setSelectedMappedOrganization] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Loading states
  const [loadingMappedOrgs, setLoadingMappedOrgs] = useState(false);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [creating, setCreating] = useState(false);

  // Fetch typing centers on mount
  useEffect(() => {
    fetchTypingCenters();
  }, []);

  // Fetch organizations when typing center changes
  useEffect(() => {
    if (selectedTypingCenterId) {
      fetchMappedOrganizations(selectedTypingCenterId);
    }
  }, [selectedTypingCenterId]);

  // Fetch work orders when internal organization changes
  useEffect(() => {
    if (selectedOrganization && selectedTypingCenterId) {
      fetchInvoiceableWorkOrders();
    }
  }, [selectedOrganization, selectedTypingCenterId]);

  const fetchTypingCenters = async () => {
    try {
      const response = await ApiService.getTypingCenters();
      if (response.data?.data) {
        setTypingCenters(response.data.data);
        if (response.data.data.length > 0) {
          const defaultTc = currentUser?.typingCenterId || response.data.data[0].id;
          setSelectedTypingCenterId(defaultTc);
        }
      }
    } catch (err) {
      console.error('Error fetching typing centers:', err);
      setError('Failed to load typing centers');
    }
  };

  const fetchMappedOrganizations = async (typingCenterId) => {
    setLoadingMappedOrgs(true);
    setMappedOrganizations([]);
    setSelectedMappedOrganization(null);
    try {
      const mappingsResponse = await ApiService.get(`/api/mappings?typingCenterId=${typingCenterId}`);
      if (mappingsResponse.data?.data) {
        const orgs = mappingsResponse.data.data.map(m => ({
          id: m.organization_id,
          name: m.organization_name,
          code: m.organization_code || ''
        }));
        setMappedOrganizations(orgs);
        if (orgs.length === 1) {
          setSelectedMappedOrganization(orgs[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching mapped organizations:', err);
      setError('Failed to load mapped organizations');
    } finally {
      setLoadingMappedOrgs(false);
    }
  };

  const fetchInvoiceableWorkOrders = async () => {
    setLoadingWorkOrders(true);
    setSelectedWorkOrders([]);
    try {
      // Work orders store internal_organizations.id in organization_id, so this call
      // correctly filters by the selected internal organization.
      const response = await ApiService.getInvoiceableWorkOrders(
        selectedOrganization.id,
        selectedTypingCenterId
      );
      const invoiceable = response.data?.data || [];
      setWorkOrders(invoiceable);
    } catch (err) {
      console.error('Error fetching invoiceable work orders:', err);
      setError('Failed to load work orders');
      setWorkOrders([]);
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  useEffect(() => {
    setSelectedOrganization(null);
    setWorkOrders([]);
    setSelectedWorkOrders([]);
  }, [selectedTypingCenterId]);

  // Auto-select when the tenant has exactly one customer — but never mid-search.
  useEffect(() => {
    if (!organizationSearchTerm && internalOrganizations.length === 1) {
      setSelectedOrganization((prev) => prev || internalOrganizations[0]);
    }
  }, [internalOrganizations, organizationSearchTerm]);

  const handleOrganizationChange = (event, org) => {
    setSelectedOrganization(org || null);
  };

  const handleMappedOrganizationChange = (event) => {
    const orgId = event.target.value;
    const org = mappedOrganizations.find(o => o.id === orgId);
    setSelectedMappedOrganization(org || null);
  };

  const handleToggleWorkOrder = (workOrderId) => {
    setSelectedWorkOrders(prev => {
      if (prev.includes(workOrderId)) {
        return prev.filter(id => id !== workOrderId);
      } else {
        return [...prev, workOrderId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredIds = filteredWorkOrders.map(wo => wo.id);
    setSelectedWorkOrders(filteredIds);
  };

  const handleDeselectAll = () => {
    setSelectedWorkOrders([]);
  };

  const handleCreateInvoice = async () => {
    if (selectedWorkOrders.length === 0) {
      setError('Please select at least one work order');
      return;
    }
    if (!selectedMappedOrganization) {
      setError('Please select a mapped organization to bill the invoice to');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      // Submit uses the mapped organization (stored as invoice.organizationId/name/code).
      // The backend copies the internal organization from the selected work orders into
      // invoice.internal_organization_id.
      const response = await ApiService.createInvoiceFromWorkOrders({
        typingCenterId: selectedTypingCenterId,
        organizationId: selectedMappedOrganization.id,
        organizationName: selectedMappedOrganization.name,
        organizationCode: selectedMappedOrganization.code || '',
        workOrderIds: selectedWorkOrders
      });

      if (response.data?.data) {
        const invoiceId = response.data.data.id;
        setSuccess('Invoice created successfully!');
        setTimeout(() => {
          navigate(`/invoices/${invoiceId}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter work orders by search term
  const filteredWorkOrders = workOrders.filter(wo => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const woNumber = (wo.work_order_number || wo.workOrderNumber || '').toLowerCase();
    const customerName = (wo.customer_name || wo.customerName || '').toLowerCase();
    return woNumber.includes(search) || customerName.includes(search);
  });

  // Label the client selector by the selected typing center's type — a B2B TC's "clients" are
  // themselves typing centers, so it reads "Typing Center"; otherwise "Client".
  const selectedTc = typingCenters.find(tc => tc.id === selectedTypingCenterId);
  const clientTerm = (selectedTc?.organization_type || selectedTc?.organizationType
    || currentUser?.organizationType) === 'B2B' ? 'Typing Center' : 'Client';

  // Calculate selected totals
  const selectedTotal = workOrders
    .filter(wo => selectedWorkOrders.includes(wo.id))
    .reduce((sum, wo) => sum + (parseFloat(wo.grand_total || wo.grandTotal) || 0), 0);

  const selectedSubtotal = workOrders
    .filter(wo => selectedWorkOrders.includes(wo.id))
    .reduce((sum, wo) => sum + (parseFloat(wo.subtotal) || 0), 0);

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={isMobile ? 2 : 3}>
        <IconButton onClick={() => navigate('/invoices')} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant={isSmallMobile ? 'h6' : 'h5'} fontWeight={600}>
            Create Invoice from Work Orders
          </Typography>
          {!isSmallMobile && (
            <Typography variant="body2" color="text.secondary">
              Select multiple work orders to combine into a single invoice
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Organization Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {isPlatform && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Typing Center</InputLabel>
                <Select
                  value={selectedTypingCenterId}
                  label="Typing Center"
                  onChange={(e) => setSelectedTypingCenterId(e.target.value)}
                >
                  {typingCenters.map(tc => (
                    <MenuItem key={tc.id} value={tc.id}>
                      {tc.name} ({tc.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={isPlatform ? 6 : 12} md={isPlatform ? 8 : 12}>
            <FormControl fullWidth>
              <Autocomplete
                options={internalOrganizations}
                getOptionLabel={(o) => `${o.name}${o.code ? ` (${o.code})` : ''}`}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                onInputChange={onOrganizationInputChange}
                filterOptions={organizationFilterOptions}
                loadingText="Searching..."
                noOptionsText="No organizations found"
                loading={loadingOrgs}
                renderInput={(params) => (
                  <TextField {...params} label={clientTerm} placeholder="Search by name or TRN..." />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Bill To (Mapped Organization)</InputLabel>
              <Select
                value={selectedMappedOrganization?.id || ''}
                label="Bill To (Mapped Organization)"
                onChange={handleMappedOrganizationChange}
                disabled={loadingMappedOrgs || mappedOrganizations.length === 0}
                sx={{
                  backgroundColor: selectedMappedOrganization ? '#f0f7ff' : 'inherit',
                  '& .MuiSelect-select': { fontWeight: selectedMappedOrganization ? 600 : 400 }
                }}
              >
                {loadingMappedOrgs ? (
                  <MenuItem value="" disabled>Loading mapped organizations...</MenuItem>
                ) : mappedOrganizations.length === 0 ? (
                  <MenuItem value="" disabled>No mapped organizations found</MenuItem>
                ) : (
                  mappedOrganizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name} {org.code && `(${org.code})`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Work Orders Section */}
      {selectedOrganization && (
        <Paper sx={{ p: isMobile ? 2 : 3 }}>
          {/* Header */}
          <Box
            display="flex"
            flexDirection={isSmallMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={isSmallMobile ? 'stretch' : 'center'}
            gap={isSmallMobile ? 1.5 : 0}
            mb={2}
          >
            <Box mb={isSmallMobile ? 1 : 0}>
              <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight={600}>
                Completed Work Orders for {selectedOrganization.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select work orders to include in the invoice (same organization only)
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSelectAll}
                disabled={filteredWorkOrders.length === 0}
                sx={{ fontSize: isSmallMobile ? '0.7rem' : '0.8rem' }}
              >
                Select All ({filteredWorkOrders.length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDeselectAll}
                sx={{
                  borderColor: '#666',
                  color: 'text.primary',
                  fontSize: isSmallMobile ? '0.7rem' : '0.8rem',
                  '&:hover': { borderColor: '#333', backgroundColor: 'action.hover' }
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by work order # or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          {/* Work Orders Table/Cards */}
          {loadingWorkOrders ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : filteredWorkOrders.length === 0 ? (
            <Box textAlign="center" py={4}>
              <WorkOrderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                {workOrders.length === 0
                  ? 'No completed work orders available for invoicing.'
                  : 'No work orders match your search.'}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Only COMPLETED work orders that haven't been invoiced are shown.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile Card View */
            <Stack spacing={1.5} sx={{ maxHeight: 'calc(100vh - 450px)', overflowY: 'auto' }}>
              {filteredWorkOrders.map((wo) => {
                const isSelected = selectedWorkOrders.includes(wo.id);
                return (
                  <Card
                    key={wo.id}
                    variant="outlined"
                    onClick={() => handleToggleWorkOrder(wo.id)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? 'rgba(87, 148, 242, 0.08)' : 'inherit',
                      border: isSelected ? '2px solid #5794F2' : '1px solid #e0e0e0'
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleWorkOrder(wo.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ p: 0, mt: 0.5 }}
                        />
                        <Box flex={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                            <Typography fontWeight={600} sx={{ fontSize: fontSize.sm }}>
                              {wo.work_order_number || wo.workOrderNumber}
                            </Typography>
                            <Chip label="Completed" size="small" color="success" sx={{ height: 22, fontSize: fontSize.xs }} />
                          </Box>
                          <Typography color="text.secondary" sx={{ fontSize: fontSize.xs }}>
                            {wo.customer_name || wo.customerName || '-'}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                            <Typography color="text.secondary" sx={{ fontSize: fontSize.xs }}>
                              {formatDate(wo.order_date || wo.orderDate)}
                            </Typography>
                            <Typography fontWeight={700} color="primary" sx={{ fontSize: fontSize.sm }}>
                              {formatCurrency(wo.grand_total || wo.grandTotal)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            /* Desktop Table View */
            <TableContainer sx={{ maxHeight: 'calc(100vh - 500px)', minHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead sx={getBrandedTableHeadSx(theme, isPlatform ? 'platform' : 'tenant')}>
                  <TableRow sx={{ '& th': { fontWeight: 600, fontSize: fontSize.xs } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedWorkOrders.length > 0 && selectedWorkOrders.length === filteredWorkOrders.length}
                        indeterminate={selectedWorkOrders.length > 0 && selectedWorkOrders.length < filteredWorkOrders.length}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        size="small"
                        sx={{ color: 'inherit', '&.Mui-checked': { color: 'inherit' }, '&.MuiCheckbox-indeterminate': { color: 'inherit' } }}
                      />
                    </TableCell>
                    <TableCell>Work Order #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">VAT</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWorkOrders.map((wo) => {
                    const isSelected = selectedWorkOrders.includes(wo.id);
                    return (
                      <TableRow
                        key={wo.id}
                        hover
                        onClick={() => handleToggleWorkOrder(wo.id)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'rgba(87, 148, 242, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ py: 1 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleWorkOrder(wo.id)}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                          <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                            {wo.work_order_number || wo.workOrderNumber}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                            {formatDate(wo.order_date || wo.orderDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {wo.customer_name || wo.customerName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {formatCurrency(wo.subtotal)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {formatCurrency(wo.tax_total || wo.taxTotal)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>
                            {formatCurrency(wo.grand_total || wo.grandTotal)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Summary Footer */}
          <Box sx={{ mt: 2, p: isMobile ? 1.5 : 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Available: <strong>{workOrders.length}</strong> work orders
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent={isMobile ? 'space-between' : 'flex-end'} alignItems="center" gap={3}>
                  <Box textAlign={isMobile ? 'left' : 'right'}>
                    <Typography variant="body2" color="text.secondary">
                      Selected Work Orders
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedWorkOrders.length}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      {formatCurrency(selectedTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box
            display="flex"
            flexDirection={isSmallMobile ? 'column-reverse' : 'row'}
            justifyContent="flex-end"
            gap={isSmallMobile ? 1 : 2}
            mt={isMobile ? 2 : 3}
          >
            <Button
              variant="outlined"
              fullWidth={isSmallMobile}
              onClick={() => navigate('/invoices')}
              sx={{
                borderColor: '#666',
                color: 'text.primary',
                '&:hover': { borderColor: '#333', backgroundColor: 'action.hover' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size={isSmallMobile ? 'medium' : 'large'}
              fullWidth={isSmallMobile}
              startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <InvoiceIcon />}
              onClick={handleCreateInvoice}
              disabled={creating || selectedWorkOrders.length === 0 || !selectedMappedOrganization}
            >
              {creating ? 'Creating...' : `Create Invoice (${selectedWorkOrders.length} work orders)`}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Empty State */}
      {!selectedOrganization && (
        <Paper sx={{ p: isMobile ? 4 : 6, textAlign: 'center' }}>
          <WorkOrderIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.disabled', mb: isMobile ? 1.5 : 2 }} />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" gutterBottom>
            Select an Organization
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
            Choose an organization from the dropdown above to view completed work orders.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CreateInvoiceFromWorkOrders;
